/* Evergreen Keepsake — theme.js (vanilla, no dependencies) */
(() => {
  'use strict';

  const T = window.theme || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Money ---------------- */
  function formatMoney(cents, format = T.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Math.round(Number(cents) || 0);
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? w + dec + frac : w;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(cents, 2);
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmt(cents, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ---------------- Toast ---------------- */
  let toastTimer;
  function toast(msg) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  }

  /* ---------------- Drawers ---------------- */
  let lastFocus = null;
  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    const panel = $('.drawer__panel', drawer);
    if (panel) setTimeout(() => panel.focus(), 50);
  }
  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!$('.drawer.is-open')) document.body.classList.remove('is-locked');
    if (lastFocus) lastFocus.focus();
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(document.getElementById(opener.dataset.drawerOpen)); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const d = closer.closest('.drawer');
      if (d) { if (closer.tagName === 'A' && closer.getAttribute('href') && !closer.getAttribute('href').startsWith('#')) return; e.preventDefault(); closeDrawer(d); }
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') $$('.drawer.is-open').forEach(closeDrawer); });

  /* ---------------- Cart ---------------- */
  const cartDrawer = () => $('[data-cart-drawer]');
  const isDrawerMode = () => T.settings && T.settings.cartType === 'drawer' && !!cartDrawer();

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-open]');
    if (btn && isDrawerMode()) { e.preventDefault(); openDrawer(cartDrawer()); }
  });

  async function refreshCart({ open = false } = {}) {
    if ($('[data-cart-page]')) { window.location.reload(); return; }
    try {
      const res = await fetch(`${T.routes.root}?sections=cart-drawer`.replace('//?', '/?'));
      const data = await res.json();
      const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const fresh = $('.drawer__panel', html);
      const drawer = cartDrawer();
      if (fresh && drawer) $('.drawer__panel', drawer).innerHTML = fresh.innerHTML;
      const cart = await (await fetch(`${T.routes.cart}.js`)).json();
      const isProtection = (i) => T.settings && T.settings.protectionHandle && i.handle === T.settings.protectionHandle;
      const count = cart.items.reduce((n, i) => (isProtection(i) ? n : n + i.quantity), 0);
      // Shipping protection on its own makes no sense: drop it once the cart has nothing else.
      const orphan = count === 0 ? cart.items.find(isProtection) : null;
      if (orphan) { await setLine(orphan.key, 0); return refreshCart({ open }); }
      $$('[data-cart-count]').forEach((el) => { el.dataset.count = count; el.textContent = count > 0 ? count : ''; });
      if (open && drawer) openDrawer(drawer);
    } catch (err) { console.error(err); }
  }

  function setLine(key, quantity) {
    return fetch(`${T.routes.cartChange}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
  }

  async function cartChange(line, quantity) {
    const res = await fetch(`${T.routes.cartChange}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) })
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); toast(j.description || T.strings.error); }
    await refreshCart();
  }

  async function cartAdd(items) {
    const res = await fetch(`${T.routes.cartAdd}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.description || json.message || T.strings.error);
    return json;
  }

  document.addEventListener('click', async (e) => {
    const q = e.target.closest('[data-qty-change]');
    if (q) { e.preventDefault(); q.disabled = true; await cartChange(q.dataset.qtyChange, q.dataset.qty); return; }
    const add = e.target.closest('[data-quick-add]');
    if (add) {
      e.preventDefault(); add.classList.add('is-loading');
      try { await cartAdd([{ id: Number(add.dataset.quickAdd), quantity: 1 }]); await refreshCart({ open: isDrawerMode() }); }
      catch (err) { toast(err.message); }
      add.classList.remove('is-loading');
    }
  });
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (input) { await cartChange(input.dataset.qtyInput, input.value); return; }
    const protection = e.target.closest('[data-protection-toggle]');
    if (protection) {
      protection.disabled = true;
      try {
        if (protection.checked) await cartAdd([{ id: Number(protection.dataset.variant), quantity: 1 }]);
        else if (protection.dataset.key) await setLine(protection.dataset.key, 0);
      } catch (err) { toast(err.message); protection.checked = !protection.checked; }
      await refreshCart();
      return;
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) {
      fetch(`${T.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
    }
  });

  /* ---------------- Buy box / bundle tiers ---------------- */
  function initBuyBox(box) {
    const jsonEl = $('[data-product-json]', box);
    if (!jsonEl) return;
    const product = JSON.parse(jsonEl.textContent);
    const byId = new Map(product.variants.map((v) => [String(v.id), v]));
    const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
    const form = $('[data-product-form]', box);
    const atc = $('[data-atc]', box);
    const atcLabel = $('[data-atc-label]', box);
    const baseLabel = atcLabel ? atcLabel.textContent.trim() : (T.strings.addToCart || 'Add to cart');
    const tiers = $$('[data-tier]', box);
    const section = box.closest('[data-product-section]') || box.closest('.shopify-section') || document;
    const sticky = $('[data-sticky-atc]', section);
    const stickyMeta = sticky ? $('[data-sticky-meta]', sticky) : null;

    const selectedTier = () => tiers.find((t) => t.classList.contains('is-selected')) || null;

    function pickerValue(picker) {
      const checked = $('input[data-variant-input]:checked', picker);
      if (checked) return checked.value;
      const sel = $('select[data-variant-input]', picker);
      return sel ? sel.value : null;
    }

    function currentItems() {
      const tier = selectedTier();
      const qty = tier ? Number(tier.dataset.qty) || 1 : 1;
      const pickers = tier ? $$('[data-tree-picker]', tier) : [];
      const counts = new Map();
      if (pickers.length) {
        pickers.forEach((p) => { const id = pickerValue(p) || String(firstAvailable.id); counts.set(id, (counts.get(id) || 0) + 1); });
      } else {
        const fallback = $('[data-fallback-id]', box);
        counts.set(fallback ? fallback.value : String(firstAvailable.id), qty);
      }
      return Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    }

    function tierTotals(tier, items) {
      const disc = Number(tier ? tier.dataset.discount : 0) || 0;
      let total = 0; let compare = 0;
      items.forEach(({ id, quantity }) => {
        const v = byId.get(String(id)) || firstAvailable;
        total += v.price * quantity;
        compare += Math.max(v.compare_at_price || 0, v.price) * quantity;
      });
      return { total: Math.round(total * (100 - disc) / 100), compare };
    }

    function update() {
      const tier = selectedTier();
      const items = currentItems();
      const { total, compare } = tierTotals(tier, items);
      const qty = items.reduce((s, i) => s + i.quantity, 0);
      const allAvailable = items.every(({ id }) => (byId.get(String(id)) || {}).available !== false);

      if (tier) {
        const t = $('[data-tier-total]', tier); if (t) t.textContent = formatMoney(total);
        const c = $('[data-tier-compare]', tier); if (c) c.textContent = formatMoney(compare);
        const each = $('[data-tier-each]', tier); if (each && qty > 0) each.textContent = `${formatMoney(Math.round(total / qty))} each`;
      }
      if (atc) {
        atc.disabled = !allAvailable;
        if (atcLabel) atcLabel.textContent = allAvailable ? `${baseLabel} · ${formatMoney(total)}` : T.strings.soldOut;
      }
      if (stickyMeta) stickyMeta.textContent = `${qty} ${qty > 1 ? 'trees' : 'tree'} · ${formatMoney(total)}`;
      const fq = $('[data-fallback-qty]', box); if (fq) fq.value = qty;
      const fid = $('[data-fallback-id]', box); if (fid && items[0]) fid.value = items[0].id;
    }

    tiers.forEach((tier) => {
      tier.addEventListener('click', (e) => {
        if (e.target.closest('[data-tree-picker]') && tier.classList.contains('is-selected')) return;
        tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
        const radio = $('input[type="radio"][name^="tier-"]', tier); if (radio) radio.checked = true;
        update();
      });
    });

    box.addEventListener('change', (e) => {
      const tierRadio = e.target.closest('input[type="radio"][name^="tier-"]');
      if (tierRadio) {
        const tier = tierRadio.closest('[data-tier]');
        tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
        update();
        return;
      }
      const input = e.target.closest('[data-variant-input]');
      if (!input) return;
      update();
      const v = byId.get(String(input.value));
      if (v && v.image) document.dispatchEvent(new CustomEvent('gallery:goto-media', { detail: { mediaId: v.image } }));
    });

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!atc || atc.disabled) return;
        atc.classList.add('is-loading');
        const prev = atcLabel ? atcLabel.textContent : '';
        if (atcLabel) atcLabel.textContent = T.strings.adding;
        try {
          await cartAdd(currentItems());
          if (isDrawerMode()) await refreshCart({ open: true });
          else window.location.href = T.routes.cart;
        } catch (err) { toast(err.message); }
        atc.classList.remove('is-loading');
        if (atcLabel) atcLabel.textContent = prev;
        update();
      });
    }

    if (sticky && atc) {
      const btn = $('[data-sticky-btn]', sticky);
      if (btn) btn.addEventListener('click', () => {
        if (form && typeof form.requestSubmit === 'function') form.requestSubmit(); else if (form) form.submit();
      });
      const io = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        const show = !entry.isIntersecting && below;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (btn) btn.tabIndex = show ? 0 : -1;
      });
      io.observe(atc);
    }

    update();
  }
  $$('[data-buy-box]').forEach(initBuyBox);

  /* scroll-to-buy buttons */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-scroll-to-buy]');
    if (!a || a.getAttribute('href') !== '#MainContent') return;
    const target = $('[data-tiers]') || $('[data-buy-box]');
    if (!target) return;
    e.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' });
  });

  /* ---------------- Gallery ---------------- */
  $$('[data-gallery]').forEach((gallery) => {
    const track = $('[data-gallery-track]', gallery);
    if (!track) return;
    const slides = $$('.gallery__slide', track);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const dotsWrap = $('[data-gallery-dots]', gallery);
    let dots = [];
    if (dotsWrap && slides.length > 1) {
      dotsWrap.innerHTML = slides.map((_, i) => `<button type="button" class="gallery__dot${i === 0 ? ' is-active' : ''}" aria-label="Slide ${i + 1}"></button>`).join('');
      dots = $$('.gallery__dot', dotsWrap);
      dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    }
    let index = 0;
    function go(i) {
      index = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0), behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    function setActive(i) {
      index = i;
      dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
      slides.forEach((s, k) => { if (k !== i) { const v = $('video', s); if (v && !v.paused) v.pause(); } });
    }
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let best = 0; let bestDist = Infinity;
        slides.forEach((s, k) => { const c = s.offsetLeft - track.offsetLeft + s.clientWidth / 2; const d = Math.abs(c - center); if (d < bestDist) { bestDist = d; best = k; } });
        if (best !== index) setActive(best);
      });
    }, { passive: true });
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.galleryThumb))));
    const prev = $('[data-gallery-prev]', gallery); const next = $('[data-gallery-next]', gallery);
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));
    if (slides.length < 2) { if (prev) prev.hidden = true; if (next) next.hidden = true; }
    document.addEventListener('gallery:goto-media', (e) => {
      const k = slides.findIndex((s) => s.dataset.mediaId === String(e.detail.mediaId));
      if (k > -1) go(k);
    });
  });

  /* ---------------- Horizontal scrollers ---------------- */
  // Endless loop: one cloned set before and after the real cards; once scrolling settles
  // outside the middle set we jump by exactly one set width, which looks identical.
  function initLoop(sc) {
    const originals = Array.from(sc.children);
    if (originals.length < 2 || (window.Shopify && Shopify.designMode)) return;
    const makeClone = (node) => {
      const c = node.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.removeAttribute('data-shopify-editor-block');
      $$('a, button, video, [tabindex]', c).forEach((el) => el.setAttribute('tabindex', '-1'));
      return c;
    };
    originals.forEach((n) => sc.insertBefore(makeClone(n), originals[0]));
    originals.forEach((n) => sc.appendChild(makeClone(n)));
    const setWidth = () => originals[0].offsetLeft - sc.firstElementChild.offsetLeft;
    const jumpBy = (dx) => {
      sc.style.scrollSnapType = 'none';
      sc.scrollLeft += dx;
      void sc.offsetWidth;
      sc.style.scrollSnapType = '';
    };
    requestAnimationFrame(() => jumpBy(setWidth()));
    let settle;
    sc.addEventListener('scroll', () => {
      clearTimeout(settle);
      settle = setTimeout(() => {
        const w = setWidth();
        if (sc.scrollLeft < w * 0.5) jumpBy(w);
        else if (sc.scrollLeft > w * 1.5) jumpBy(-w);
      }, 120);
    }, { passive: true });
    window.addEventListener('resize', () => {
      const w = setWidth();
      if (w > 0 && (sc.scrollLeft < w * 0.5 || sc.scrollLeft > w * 1.5)) jumpBy(w + (sc.scrollLeft % w) - sc.scrollLeft);
    });
  }

  $$('[data-scroller]').forEach((sc) => {
    if (sc.hasAttribute('data-scroller-loop')) initLoop(sc);
    const wrap = sc.parentElement;
    const step = () => { const first = sc.firstElementChild; return first ? first.getBoundingClientRect().width + 14 : sc.clientWidth * 0.8; };
    const prev = $('[data-scroller-prev]', wrap); const next = $('[data-scroller-next]', wrap);
    if (prev) prev.addEventListener('click', () => sc.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => sc.scrollBy({ left: step(), behavior: 'smooth' }));
  });

  /* ---------------- UGC videos ---------------- */
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-ugc]');
    if (!card) return;
    const video = $('video', card);
    if (!video) return;
    $$('[data-ugc] video').forEach((v) => { if (v !== video) { v.pause(); v.closest('[data-ugc]').classList.remove('is-playing'); } });
    if (video.paused) { video.muted = true; video.controls = true; video.play(); card.classList.add('is-playing'); }
    else { video.pause(); card.classList.remove('is-playing'); }
  });

  /* ---------------- Countdown to Christmas cutoff ---------------- */
  const pad = (n) => String(n).padStart(2, '0');
  const cutoffs = $$('[data-cutoff]');
  if (cutoffs.length) {
    const tick = () => {
      cutoffs.forEach((el) => {
        const end = Date.parse(el.dataset.date);
        if (isNaN(end)) { const cd = $('[data-countdown]', el); if (cd) cd.hidden = true; return; }
        let diff = Math.floor((end - Date.now()) / 1000);
        const before = $('[data-cutoff-before]', el); const after = $('[data-cutoff-after]', el); const cd = $('[data-countdown]', el);
        if (diff <= 0) { if (before) before.hidden = true; if (after) after.hidden = false; if (cd) cd.hidden = true; return; }
        const d = Math.floor(diff / 86400); diff -= d * 86400;
        const h = Math.floor(diff / 3600); diff -= h * 3600;
        const m = Math.floor(diff / 60); const s = diff - m * 60;
        const set = (sel, v) => { const n = $(sel, el); if (n) n.textContent = pad(v); };
        set('[data-d]', d); set('[data-h]', h); set('[data-m]', m); set('[data-s]', s);
      });
    };
    tick(); setInterval(tick, 1000);
  }

  /* ---------------- Delivery estimate ---------------- */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6) added++; }
    return d;
  }
  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  $$('[data-delivery]').forEach((el) => {
    const now = new Date();
    const ship = addBusinessDays(now, 1);
    const min = addBusinessDays(now, Number(el.dataset.min) || 4);
    const max = addBusinessDays(now, Number(el.dataset.max) || 7);
    const s = $('[data-delivery-ship]', el); if (s) s.textContent = fmtDate(ship);
    const a = $('[data-delivery-arrive]', el); if (a) a.textContent = `${fmtDate(min)} – ${fmtDate(max)}`;
    const t = $('[data-delivery-today]', el); if (t) t.textContent = fmtDate(now);
  });

  /* ---------------- Announcement rotation ---------------- */
  $$('[data-announcement]').forEach((bar) => {
    const msgs = $$('.announcement__msg', bar);
    if (msgs.length < 2) return;
    let i = 0;
    setInterval(() => { msgs[i].classList.remove('is-active'); i = (i + 1) % msgs.length; msgs[i].classList.add('is-active'); }, Number(bar.dataset.interval) || 4000);
  });

  /* ---------------- Reviews: filter + show more ---------------- */
  $$('[data-reviews]').forEach((wrap) => {
    const perPage = Number(wrap.dataset.perPage) || 6;
    const cards = $$('[data-review]', wrap);
    const more = $('[data-reviews-more]', wrap);
    let filter = 'all'; let shown = perPage;
    const render = () => {
      const matching = cards.filter((c) => filter === 'all' || c.dataset.tag === filter);
      cards.forEach((c) => { c.hidden = true; });
      matching.slice(0, shown).forEach((c) => { c.hidden = false; c.classList.add('is-in'); });
      if (more) more.parentElement.hidden = matching.length <= shown;
    };
    $$('[data-review-filter]', wrap).forEach((chip) => chip.addEventListener('click', () => {
      $$('[data-review-filter]', wrap).forEach((c) => c.classList.toggle('is-active', c === chip));
      filter = chip.dataset.reviewFilter; shown = perPage; render();
    }));
    if (more) more.addEventListener('click', () => { shown += perPage; render(); });
  });

  /* ---------------- Reveal on scroll ---------------- */
  if ('IntersectionObserver' in window && !reducedMotion) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    $$('.reveal').forEach((el) => io.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------------- Snow ---------------- */
  const canvas = $('[data-snow]');
  if (canvas && !reducedMotion) {
    const ctx = canvas.getContext('2d');
    let w, h, flakes, running = true;
    const count = () => (window.innerWidth < 750 ? 28 : 60);
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * dpr; canvas.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const make = () => ({ x: Math.random() * w, y: Math.random() * h, r: Math.random() * 2.2 + 0.8, s: Math.random() * 0.6 + 0.25, d: Math.random() * Math.PI * 2, o: Math.random() * 0.5 + 0.35 });
    resize(); flakes = Array.from({ length: count() }, make);
    window.addEventListener('resize', () => { resize(); flakes = Array.from({ length: count() }, make); });
    document.addEventListener('visibilitychange', () => { running = !document.hidden; if (running) requestAnimationFrame(loop); });
    function loop() {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        f.y += f.s; f.d += 0.01; f.x += Math.sin(f.d) * 0.35;
        if (f.y > h + 5) { f.y = -5; f.x = Math.random() * w; }
        ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${f.o})`;
        ctx.shadowColor = 'rgba(180,200,220,.6)'; ctx.shadowBlur = 3;
        ctx.fill();
      }
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
  }
})();
